import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Patient } from '../../services/api';
import { getRequirementStyle } from '../../config/requirementConfig';
import './PatientDetailModal.css';

interface PatientDetailModalProps {
  patient: Patient;
  onClose: () => void;
}

function PatientDetailModal({ patient, onClose }: PatientDetailModalProps) {
  const { t } = useTranslation();
  return (
    <div className="patient-detail-modal-overlay">
      <div className="patient-detail-modal">
        <div className="modal-header">
          <h2>{t('patients.patientDetails')}</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          {/* Basic Information */}
          <div className="detail-section">
            <h3>{t('patients.patientId')}</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>{t('patients.patientId')}:</label>
                <span>{patient.patientId || 'Pending Approval'}</span>
              </div>
              <div className="detail-item">
                <label>{t('patients.firstName')}:</label>
                <span>{patient.patientInitials}</span>
              </div>
              <div className="detail-item">
                <label>{t('patients.age')}:</label>
                <span>{patient.patientAge} years</span>
              </div>
              <div className="detail-item">
                <label>{t('patients.gender')}:</label>
                <span>{patient.patientGender}</span>
              </div>
              <div className="detail-item">
                <label>{t('common.status')}:</label>
                <span>{patient.patientStatus}</span>
              </div>
              {patient.sendingNation && (
                <div className="detail-item">
                  <label>{t('common.nation')}:</label>
                  <span>{patient.sendingNation}</span>
                </div>
              )}
            </div>
          </div>

          {/* Medical Information */}
          <div className="detail-section">
            <h3>{t('patients.medicalHistory')}</h3>
            <div className="detail-grid">
              <div className="detail-item full-width">
                <label>{t('patients.diagnosis')}:</label>
                <span>{patient.medicalPrimaryConditionDiagnosis}</span>
              </div>
              {patient.medicalSecondaryConditionText && (
                <div className="detail-item full-width">
                  <label>{t('patients.condition')}:</label>
                  <span>{patient.medicalSecondaryConditionText}</span>
                </div>
              )}
              {patient.medicalConditionDescription && (
                <div className="detail-item full-width">
                  <label>{t('common.description')}:</label>
                  <span>{patient.medicalConditionDescription}</span>
                </div>
              )}
              <div className="detail-item">
                <label>{t('common.priority')}:</label>
                <span className={`priority-badge priority-${patient.medicalPriority.toLowerCase()}`}>
                  {patient.medicalPriority}
                </span>
              </div>
              <div className="detail-item">
                <label>{t('patients.mobility')}:</label>
                <span>{patient.medicalDependency}</span>
              </div>
              <div className="detail-item">
                <label>{t('requests.transportDetails')}:</label>
                <span>{patient.medicalTransport}</span>
              </div>
            </div>
          </div>

          {/* Medical Requirements */}
          {patient.medicalRequirements && patient.medicalRequirements.length > 0 && (
            <div className="detail-section">
              <h3>{t('patients.treatmentNeeded')}</h3>
              <div className="requirements-list">
                {patient.medicalRequirements.map((req) => {
                  const style = getRequirementStyle(req.requirementType);
                  return (
                    <span
                      key={req.id}
                      className="requirement-badge"
                      style={{ backgroundColor: style.bg, color: style.color }}
                      title={req.requirementType.replace(/_/g, ' ')}
                    >
                      {style.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Companions */}
          {patient.patientCompanionsCount > 0 && (
            <div className="detail-section">
              <h3>{t('requests.accompaniment')}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>{t('requests.accompaniment')}:</label>
                  <span>{patient.patientCompanionsCount}</span>
                </div>
                {patient.patientCompanionsText && (
                  <div className="detail-item full-width">
                    <label>{t('common.details')}:</label>
                    <span>{patient.patientCompanionsText}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Clinical Narrative */}
          {patient.narrativeClinical && (
            <div className="detail-section">
              <h3>{t('patients.medicalHistory')}</h3>
              <div className="narrative-box">
                {patient.narrativeClinical}
              </div>
            </div>
          )}

          {/* Additional Remarks */}
          {patient.narrativeAdditionalRemarks && (
            <div className="detail-section">
              <h3>{t('common.notes')}</h3>
              <div className="narrative-box">
                {patient.narrativeAdditionalRemarks}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PatientDetailModal;
